import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { Profile } from "../types";
import { FieldSyncBadge } from "./FieldSyncBadge";
import {
  Aside,
  AsideCard,
  Badge,
  Botao,
  BotaoPequeno,
  Chip,
  ChipBusca,
  ChipX,
  Coluna,
  EditorGrid,
  Hint,
  Lab,
  Tip,
} from "./EditorChrome";
import {
  GabaritoState,
  appendKeywords,
  initialGabaritoState,
  removeKeyword,
  renameFileWithRandomKeyword,
  separarPalavras,
  splitKeywords,
} from "./gabarito-utils";
import { avisoDaFoto, prepararFotoParaGoogle } from "./foto-para-google";
import { getApiErrorMessage, profilesTrackingService } from "../../../services";

/* Prancheta PerfilGabarito: coluna 8/4, seções separadas por linha, fotos em grade 4:3. */

const Titulo = styled.h3`
  margin: 0;
  font-family: var(--font-sans);
  font-size: 22px;
  line-height: 28px;
  font-weight: 500;
  color: #1f1f1f;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Secao = styled.section`
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 22px 0;
  border-bottom: 1px solid #eef1f4;
  &:last-of-type {
    border-bottom: 0;
  }
  h4 {
    margin: 0;
    font-family: var(--font-sans);
    font-size: 16px;
    font-weight: 600;
    color: #1f1f1f;
  }
`;

const Fotos = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 10px;
`;

const Foto = styled.div`
  aspect-ratio: 4 / 3;
  border-radius: 12px;
  background: linear-gradient(135deg, #d6e4f7, #eaf1fb);
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #0b4fa8;
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
  .tag {
    position: absolute;
    left: 8px;
    bottom: 8px;
    max-width: calc(100% - 16px);
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .remover {
    position: absolute;
    top: 6px;
    right: 6px;
    width: 28px;
    height: 28px;
    border-radius: 999px;
    border: 0;
    background: rgba(255, 255, 255, 0.92);
    color: #b3261e;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    opacity: 0;
    transition: opacity 0.15s;
  }
  &:hover .remover,
  .remover:focus-visible {
    opacity: 1;
  }
  .remover .material-icons {
    font-size: 16px;
  }
`;

const AdicionarFoto = styled.label`
  aspect-ratio: 4 / 3;
  border-radius: 12px;
  background: #fff;
  border: 1px dashed #c2d7f5;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  color: #1264c7;
  cursor: pointer;
  font-family: var(--font-sans);
  font-size: 12px;
  font-weight: 500;
  &:hover {
    background: #f8fafd;
  }
  input {
    display: none;
  }
  .material-icons {
    font-size: 28px;
  }
`;

const CaixaDeTexto = styled.textarea`
  width: 100%;
  min-height: 132px;
  padding: 12px 14px;
  border: 1px solid #e2e6ea;
  border-radius: 12px;
  font-size: 14px;
  line-height: 22px;
  font-family: inherit;
  color: #1f1f1f;
  box-sizing: border-box;
  resize: vertical;
  &:focus {
    outline: none;
    border-color: #1264c7;
    box-shadow: 0 0 0 2px rgba(18, 100, 199, 0.25);
  }
`;

const Lote = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 14px;
  border-radius: 12px;
  background: #f7f9fc;
  border: 1px solid #e2e6ea;
  label {
    font-size: 13px;
    line-height: 20px;
    color: #3c4043;
  }
`;

const Uso = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 10px;
  background: #f7f9fc;
  font-size: 13px;
  .material-icons {
    color: #0b4fa8;
    font-size: 20px;
  }
`;

type GabaritoTabProps = {
  profile: Profile | null;
};

/**
 * Chips com × e campo "Adicionar palavra" (Enter ou vírgula adiciona; colar uma lista adiciona todas).
 * "Adicionar várias de uma vez" abre uma caixa para colar a lista inteira separada por vírgula.
 */
const Palavras: React.FC<{
  palavras: string[];
  tom?: "azul" | "verde";
  onAdicionar: (palavras: string[]) => void;
  onRemover: (palavra: string) => void;
  onRemoverTodas: () => void;
  idDoCampo: string;
}> = ({ palavras, tom = "azul", onAdicionar, onRemover, onRemoverTodas, idDoCampo }) => {
  const [rascunho, setRascunho] = useState("");
  const [emLote, setEmLote] = useState(false);
  const [lista, setLista] = useState("");
  const confirmar = () => {
    const novas = separarPalavras(rascunho);
    if (novas.length) onAdicionar(novas);
    setRascunho("");
  };
  const existentes = new Set(palavras.map((palavra) => palavra.toLowerCase()));
  const daLista = separarPalavras(lista).filter(
    (palavra, indice, todas) => todas.findIndex((outra) => outra.toLowerCase() === palavra.toLowerCase()) === indice,
  );
  const novasDaLista = daLista.filter((palavra) => !existentes.has(palavra.toLowerCase()));
  const repetidas = daLista.length - novasDaLista.length;
  const fecharLote = () => {
    setLista("");
    setEmLote(false);
  };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
        {palavras.map((palavra) => (
          <Chip key={palavra} style={tom === "verde" ? { background: "#EAF7EE", color: "#137333" } : undefined}>
            <span className="texto">{palavra}</span>
            <ChipX type="button" aria-label={`Remover ${palavra}`} style={tom === "verde" ? { color: "#137333" } : undefined} onClick={() => onRemover(palavra)}>
              <span className="material-icons">close</span>
            </ChipX>
          </Chip>
        ))}
        <ChipBusca htmlFor={idDoCampo}>
          <span className="material-icons">add</span>
          <input
            id={idDoCampo}
            value={rascunho}
            placeholder="Adicionar palavra"
            style={{ width: 160 }}
            onChange={(event) => {
              if (event.target.value.endsWith(",")) {
                const novas = separarPalavras(event.target.value);
                if (novas.length) onAdicionar(novas);
                setRascunho("");
                return;
              }
              setRascunho(event.target.value);
            }}
            onPaste={(event) => {
              const colado = event.clipboardData.getData("text");
              if (!/[,;\r\n]/.test(colado)) return;
              event.preventDefault();
              const novas = separarPalavras(rascunho + colado);
              if (novas.length) onAdicionar(novas);
              setRascunho("");
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                confirmar();
              }
            }}
            onBlur={confirmar}
          />
        </ChipBusca>
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8 }}>
        {!emLote && (
          <BotaoPequeno type="button" $variante="tonal" onClick={() => setEmLote(true)}>
            <span className="material-icons">playlist_add</span>
            Adicionar várias de uma vez
          </BotaoPequeno>
        )}
        {palavras.length > 0 && (
          <span style={{ fontSize: 12, color: "#5f6368" }}>
            {palavras.length} {palavras.length === 1 ? "palavra" : "palavras"}
          </span>
        )}
        {palavras.length > 1 && (
          <BotaoPequeno
            type="button"
            $variante="text"
            onClick={() => {
              if (window.confirm(`Remover as ${palavras.length} palavras desta lista? Só fica gravado depois de "Salvar gabarito".`)) onRemoverTodas();
            }}
          >
            Remover todas
          </BotaoPequeno>
        )}
      </div>
      {emLote && (
        <Lote>
          <label htmlFor={`${idDoCampo}-lote`}>
            Cole ou digite as palavras <b>separadas por vírgula</b>. Também vale uma por linha (copiada de uma planilha).
          </label>
          <CaixaDeTexto
            id={`${idDoCampo}-lote`}
            autoFocus
            value={lista}
            onChange={(event) => setLista(event.target.value)}
            placeholder="Exemplo: loja de pesca em Barreiras, iscas para pesca em Barreiras, molinete barato em Barreiras"
            style={{ minHeight: 110 }}
          />
          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
            <span role="status" style={{ fontSize: 13, color: novasDaLista.length ? "#137333" : "#5f6368" }}>
              {daLista.length === 0
                ? "Nenhuma palavra ainda."
                : `${novasDaLista.length} ${novasDaLista.length === 1 ? "palavra nova" : "palavras novas"}`}
              {repetidas > 0 && ` · ${repetidas} já ${repetidas === 1 ? "está" : "estão"} na lista (${repetidas === 1 ? "será ignorada" : "serão ignoradas"})`}
            </span>
            <div style={{ display: "flex", gap: 8 }}>
              <BotaoPequeno type="button" $variante="text" onClick={fecharLote}>
                Cancelar
              </BotaoPequeno>
              <BotaoPequeno
                type="button"
                $variante="filled"
                disabled={novasDaLista.length === 0}
                onClick={() => {
                  onAdicionar(novasDaLista);
                  fecharLote();
                }}
              >
                <span className="material-icons">add</span>
                {novasDaLista.length > 0 ? `Adicionar ${novasDaLista.length}` : "Adicionar"}
              </BotaoPequeno>
            </div>
          </div>
          <Hint>Depois de adicionar, clique em "Salvar gabarito" para gravar.</Hint>
        </Lote>
      )}
    </div>
  );
};

export const GabaritoTab: React.FC<GabaritoTabProps> = ({ profile }) => {
  const profileId = profile?.id;
  const [gabarito, setGabarito] = useState<GabaritoState>(initialGabaritoState);
  const [feedback, setFeedback] = useState("");
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    if (!profileId) return;
    setFeedback("");
    let active = true;
    (async () => {
      try {
        const data = await profilesTrackingService.getGabarito(profileId);
        if (!active) return;
        if (data) {
          setGabarito({
            postKeywords: data.postKeywords ?? "",
            commentKeywords: data.commentKeywords ?? "",
            postNotes: data.postNotes ?? "",
            commentNotes: data.commentNotes ?? "",
            images: Array.isArray(data.images)
              ? data.images.map((img: { id: string | number; keyword?: string; fileName?: string; url?: string }) => ({
                  id: String(img.id),
                  title: img.keyword ?? img.fileName ?? "",
                  fileName: img.fileName ?? "",
                  originalFileName: img.fileName ?? "",
                  keyword: img.keyword ?? "",
                  src: img.url ?? "",
                }))
              : [],
          });
        } else {
          setGabarito(initialGabaritoState);
        }
      } catch (error) {
        console.error("Erro ao carregar gabarito:", error);
        if (active) setGabarito(initialGabaritoState);
      }
    })();
    return () => {
      active = false;
    };
  }, [profileId]);

  const postKeywords = useMemo(() => splitKeywords(gabarito.postKeywords), [gabarito.postKeywords]);
  const commentKeywords = useMemo(() => splitKeywords(gabarito.commentKeywords), [gabarito.commentKeywords]);

  const updateField = (field: keyof GabaritoState, value: string) => {
    setGabarito((prev) => ({ ...prev, [field]: value }));
  };

  const handleAddKeywords = (field: "postKeywords" | "commentKeywords", keywords: string[]) => {
    setGabarito((prev) => ({ ...prev, [field]: appendKeywords(prev[field], keywords) }));
  };

  const handleClearKeywords = (field: "postKeywords" | "commentKeywords") => {
    setGabarito((prev) => ({ ...prev, [field]: "" }));
  };

  const handleRemoveKeyword = (field: "postKeywords" | "commentKeywords", keyword: string) => {
    setGabarito((prev) => ({ ...prev, [field]: removeKeyword(prev[field], keyword) }));
  };

  const handleSave = async () => {
    if (!profileId) return;
    try {
      setSalvando(true);
      await profilesTrackingService.saveGabaritoText(profileId, {
        postKeywords: gabarito.postKeywords,
        commentKeywords: gabarito.commentKeywords,
        postNotes: gabarito.postNotes,
        commentNotes: gabarito.commentNotes,
      });
      setFeedback("Gabarito salvo para este perfil.");
    } catch (error) {
      console.error("Erro ao salvar gabarito:", error);
      setFeedback(`Erro ao salvar Gabarito: ${getApiErrorMessage(error, "motivo nao identificado.")}`);
    } finally {
      setSalvando(false);
    }
  };

  const handleRemoveImage = async (id: string) => {
    if (profileId) {
      try {
        await profilesTrackingService.deleteGabaritoImage(profileId, id);
      } catch (error) {
        console.error("Erro ao remover imagem do gabarito:", error);
      }
    }
    setGabarito((prev) => ({ ...prev, images: prev.images.filter((image) => image.id !== id) }));
  };

  const handleImageUpload = (files: FileList | null) => {
    const file = files?.[0];
    if (!file || !profileId) return;

    const { file: renamedFile, keyword } = renameFileWithRandomKeyword(
      file,
      postKeywords.length > 0 ? postKeywords : [profile?.name ?? "imagem"],
    );

    (async () => {
      let src: string;
      let fileName = renamedFile.name;
      let aviso = "";
      try {
        const foto = await prepararFotoParaGoogle(file, renamedFile.name);
        src = foto.dataUrl;
        fileName = foto.nome;
        aviso = avisoDaFoto(foto);
      } catch (error) {
        setFeedback(error instanceof Error ? error.message : "Nao foi possivel ler a imagem.");
        return;
      }
      try {
        const saved = await profilesTrackingService.addGabaritoImage(profileId, {
          keyword,
          fileName,
          src,
        });
        setGabarito((prev) => ({
          ...prev,
          images: [
            ...prev.images,
            {
              id: String(saved?.id ?? `${fileName}-${Date.now()}`),
              title: keyword,
              fileName,
              originalFileName: file.name,
              keyword,
              src: saved?.url ?? src,
            },
          ],
        }));
        setFeedback(`Imagem ${fileName} adicionada ao gabarito.${aviso ? ` ${aviso}` : ""}`);
      } catch (error) {
        console.error("Erro ao adicionar imagem do gabarito:", error);
        setFeedback("Nao foi possivel adicionar a imagem. Tente novamente.");
      }
    })();
  };

  const completude = [
    { rotulo: "Pelo menos 1 foto no banco", ok: gabarito.images.length > 0 },
    { rotulo: "3 ou mais palavras-chave principais", ok: postKeywords.length >= 3 },
    { rotulo: "3 ou mais palavras para respostas", ok: commentKeywords.length >= 3 },
    { rotulo: "Preferências de conteúdo", ok: gabarito.postNotes.trim().length > 0 },
    { rotulo: "Como responder comentários", ok: gabarito.commentNotes.trim().length > 0 },
  ];
  const feitos = completude.filter((item) => item.ok).length;
  const faltando = completude.find((item) => !item.ok);
  const erro = feedback.startsWith("Erro") || feedback.startsWith("Nao foi");

  return (
    <EditorGrid style={{ gridTemplateColumns: "minmax(0, 8fr) minmax(0, 4fr)" }}>
      <Coluna style={{ gap: 0 }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, paddingBottom: 8, flexWrap: "wrap" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <Titulo>
              Gabarito do perfil
              <FieldSyncBadge kind="backend" />
            </Titulo>
            <Hint>Referência que a IA e a equipe usam para criar postagens, responder avaliações e sugerir textos deste negócio. Não é publicado no Google.</Hint>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <Badge $tom="amarelo">
              <span className="material-icons">lock</span>
              Uso interno — não publicado no Google
            </Badge>
            <Botao type="button" $variante="filled" onClick={handleSave} disabled={salvando || !profileId}>
              <span className="material-icons">save</span>
              {salvando ? "Salvando..." : "Salvar gabarito"}
            </Botao>
          </div>
        </div>

        {feedback && (
          <div role="status" style={{ margin: "8px 0", padding: "10px 14px", borderRadius: 12, fontSize: 13, background: erro ? "#FCE8E6" : "#EAF7EE", color: erro ? "#B3261E" : "#137333" }}>
            {feedback}
          </div>
        )}

        <Secao>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
            <h4>Banco de fotos</h4>
            <Hint>{`${gabarito.images.length} ${gabarito.images.length === 1 ? "foto" : "fotos"} · usadas nas postagens e no cartão de avaliação`}</Hint>
          </div>
          <Fotos>
            {gabarito.images.map((image) => (
              <Foto key={image.id} title={`${image.fileName}${image.originalFileName && image.originalFileName !== image.fileName ? ` (original: ${image.originalFileName})` : ""}`}>
                {image.src ? <img src={image.src} alt={image.title} /> : <span className="material-icons" style={{ fontSize: 28 }}>image</span>}
                {image.title && <Badge $tom="cinza" className="tag">{image.title}</Badge>}
                <button type="button" className="remover" aria-label={`Remover foto ${image.title || image.fileName}`} onClick={() => handleRemoveImage(image.id)}>
                  <span className="material-icons">delete</span>
                </button>
              </Foto>
            ))}
            <AdicionarFoto>
              <span className="material-icons">add_photo_alternate</span>
              Adicionar foto
              <input
                type="file"
                accept="image/*"
                aria-label="Adicionar foto ao banco"
                onChange={(event) => {
                  handleImageUpload(event.target.files);
                  event.target.value = "";
                }}
              />
            </AdicionarFoto>
          </Fotos>
          {gabarito.images.length === 0 && <Hint>Nenhuma foto cadastrada para este perfil. As fotos recebem o nome de uma palavra-chave ao entrar.</Hint>}
        </Secao>

        <Secao>
          <h4>Palavras-chave principais</h4>
          <Hint>Termos que devem aparecer em textos e postagens. Alinhados com o relatório de palavras-chave.</Hint>
          <Palavras
            idDoCampo="gabarito-post-keywords"
            palavras={postKeywords}
            onAdicionar={(novas) => handleAddKeywords("postKeywords", novas)}
            onRemover={(palavra) => handleRemoveKeyword("postKeywords", palavra)}
            onRemoverTodas={() => handleClearKeywords("postKeywords")}
          />
        </Secao>

        <Secao>
          <h4>Palavras para respostas</h4>
          <Hint>Termos que a IA deve usar ao responder avaliações: agradecimento, atendimento, experiência.</Hint>
          <Palavras
            idDoCampo="gabarito-comment-keywords"
            tom="verde"
            palavras={commentKeywords}
            onAdicionar={(novas) => handleAddKeywords("commentKeywords", novas)}
            onRemover={(palavra) => handleRemoveKeyword("commentKeywords", palavra)}
            onRemoverTodas={() => handleClearKeywords("commentKeywords")}
          />
        </Secao>

        <Secao>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 20 }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <Lab as="label" htmlFor="gabarito-post-notes" style={{ textTransform: "none", fontSize: 13, letterSpacing: 0 }}>
                <span className="material-icons">edit_note</span>
                Preferências de conteúdo
                <FieldSyncBadge kind="backend" />
              </Lab>
              <CaixaDeTexto
                id="gabarito-post-notes"
                rows={5}
                value={gabarito.postNotes}
                onChange={(event) => updateField("postNotes", event.target.value)}
                placeholder="Preferências, termos obrigatórios e assuntos que devem aparecer nas postagens."
              />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <Lab as="label" htmlFor="gabarito-comment-notes" style={{ textTransform: "none", fontSize: 13, letterSpacing: 0 }}>
                <span className="material-icons">quickreply</span>
                Como responder comentários
                <FieldSyncBadge kind="backend" />
              </Lab>
              <CaixaDeTexto
                id="gabarito-comment-notes"
                rows={5}
                value={gabarito.commentNotes}
                onChange={(event) => updateField("commentNotes", event.target.value)}
                placeholder="Tom de voz, palavras proibidas e instruções para responder comentários."
              />
            </div>
          </div>
        </Secao>
      </Coluna>

      <Aside>
        <AsideCard>
          <Lab>Onde o gabarito é usado</Lab>
          <Uso>
            <span className="material-icons">feed</span>
            <span style={{ flex: 1 }}>Postagens geradas com IA</span>
            <Badge $tom="verde">automático</Badge>
          </Uso>
          <Uso>
            <span className="material-icons">reviews</span>
            <span style={{ flex: 1 }}>Respostas a avaliações</span>
            <Badge $tom="verde">automático</Badge>
          </Uso>
          <Uso>
            <span className="material-icons">description</span>
            <span style={{ flex: 1 }}>Sugestões de descrição e SEO</span>
            <Badge $tom="cinza">sob demanda</Badge>
          </Uso>
          <Uso>
            <span className="material-icons">qr_code_2</span>
            <span style={{ flex: 1 }}>Cartão de avaliação e mini-site</span>
            <Badge $tom="cinza">fotos</Badge>
          </Uso>
        </AsideCard>
        <AsideCard style={{ gap: 10 }}>
          <Lab>Completude do gabarito</Lab>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ flex: 1, height: 8, borderRadius: 999, background: "#EEF1F4", overflow: "hidden" }}>
              <div style={{ width: `${(feitos / completude.length) * 100}%`, height: "100%", background: feitos >= 4 ? "#1E8E3E" : "#F9AB00", transition: "width .3s" }} />
            </div>
            <span style={{ fontFamily: "var(--font-sans)", fontWeight: 500, color: feitos >= 4 ? "#137333" : "#B06000" }}>{`${feitos} de ${completude.length}`}</span>
          </div>
          <Hint>{faltando ? `Falta: ${faltando.rotulo.toLowerCase()}.` : "Gabarito completo. Quanto mais específico, menos a IA inventa."}</Hint>
        </AsideCard>
        <Tip>
          <span className="material-icons">lightbulb</span>
          Quanto mais específico o gabarito, menos a IA inventa. Cite marcas que você vende e o que não deve prometer.
        </Tip>
      </Aside>
    </EditorGrid>
  );
};
